import { useState, useEffect } from 'react';
import { Modal, Stack, Group, Button, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { GroupWithMembers } from '../../../services/api/groups';
import { groupsApi } from '../../../services/api/groups';
import { getApiErrorMessage } from '../../../services/api/client';

/**
 * MED-1294 — rename an existing group.
 *
 * Shared by both Groups display modes (AC2): the list view renders GroupCard and
 * the card view renders GroupAssignmentCard, so a single modal keeps the two
 * entry points behaving identically rather than duplicating the flow.
 *
 * The server already enforces this story's validation rules — `PUT
 * /api/cohorts/:cohortId/groups/:groupId` trims and rejects an empty name (422)
 * and rejects a duplicate within the cohort case-insensitively (409, since
 * `groups.name` is CITEXT). This modal mirrors the empty check client-side for
 * immediate feedback and surfaces the server's verdict inline rather than in a
 * toast, so the error sits next to the field the CM has to fix (AC4/AC7).
 *
 * The 409 body is generic ("Group with this name already exists") because it
 * comes from a BaseController helper shared across many controllers. AC7 asks
 * for the conflict to be named, and the client knows the name that was
 * attempted, so it composes the specific message here instead of changing
 * behavior other controllers depend on.
 */
interface RenameGroupModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  group: GroupWithMembers | null;
  /** Refresh the caller's group list once the rename has persisted. */
  onRenamed: () => void | Promise<void>;
}

export function RenameGroupModal({
  opened,
  onClose,
  cohortId,
  group,
  onRenamed,
}: RenameGroupModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed whenever a different group is opened, and clear any stale error from
  // a previous attempt.
  useEffect(() => {
    if (opened && group) {
      setName(group.name);
      setError(null);
      setTouched(false);
    }
  }, [opened, group]);

  const trimmed = name.trim();
  const isUnchanged = group ? trimmed === group.name : false;
  const canSave = trimmed.length > 0 && !isUnchanged && !saving;

  // AC4 wants an empty name "rejected with a visible message rather than
  // silently ignored" — a disabled Save button alone would be silent, so the
  // message appears as soon as the field is cleared. `touched` keeps it from
  // firing on open, when the field is seeded with the current name.
  const requiredError = touched && !trimmed ? 'Group name is required' : null;
  const displayedError = error ?? requiredError;

  const handleSave = async () => {
    if (!group) return;

    // AC4: reject empty/whitespace-only before a round trip. The server rejects
    // it too (422) — this is just faster feedback.
    if (!trimmed) {
      setError('Group name is required');
      return;
    }
    if (isUnchanged) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Send the trimmed value so what the CM sees saved is what they typed
      // (the server trims as well; this keeps the success toast honest).
      await groupsApi.update(cohortId, group.id, { name: trimmed });
      notifications.show({
        title: 'Group Renamed',
        message: `"${group.name}" is now "${trimmed}"`,
        color: 'green',
      });
      onClose();
      await onRenamed();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(
        status === 409
          ? `A group named "${trimmed}" already exists in this cohort. Group names are case-insensitive, so a different capitalization won't work either.`
          : getApiErrorMessage(err, 'Failed to rename group')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Rename Group" size="sm">
      <Stack gap="md">
        <TextInput
          label="Group Name"
          placeholder="e.g., GSALS-1"
          value={name}
          onChange={e => {
            setName(e.currentTarget.value);
            setTouched(true);
            // Clear a stale conflict message as soon as they start editing —
            // it referred to the previous value.
            if (error) setError(null);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && canSave) {
              e.preventDefault();
              void handleSave();
            }
          }}
          error={displayedError}
          data-autofocus
          required
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
